import { apiRequest, apiRoutes } from '../../../../api';
import { useEffect, useRef, useState, useMemo } from 'react';
import { MapContainer, TileLayer, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { neighborhoodOverlapsIntersection, toGeometry, buildSearchBoundaries } from './geoScopeUtils';

const LAYER_KEYS = ['nbr', 'iso', 'geo', 'intersection', 'candidates'];
const BASE_LAYERS = [
    { name: 'Standard', checked: true, url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', attribution: '&copy; OpenStreetMap contributors' },
    { name: 'Light', url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png', attribution: '&copy; CARTO' },
    { name: 'Dark', url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png', attribution: '&copy; CARTO' },
    { name: 'Satellite', url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', attribution: '&copy; Esri' },
];

function nbrToGeoJSON(nbrBoundaries) {
    var polys = [];
    nbrBoundaries.forEach(function(geom) {
        if (!geom) return;
        if (geom.type === 'Polygon') polys.push(geom.coordinates);
        else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(function(p) { polys.push(p); });
    });
    if (!polys.length) return null;
    return { type: 'MultiPolygon', coordinates: polys };
}

function isoToGeoJSON(isoPolygons) {
    var polys = [];
    isoPolygons.forEach(function(ring) {
        if (!ring || ring.length < 4) return;
        var closed = (ring[0][0] === ring[ring.length - 1][0]) ? ring : ring.concat([ring[0]]);
        polys.push([closed]);
    });
    if (!polys.length) return null;
    return { type: 'MultiPolygon', coordinates: polys };
}

function OverlayLayers({ nbrBoundaries, isoPolygons, geoAnalysis, allNeighborhoods, visibleLayers, intersection }) {
    const map = useMap();
    const groups = useRef({});

    useEffect(function() {
        LAYER_KEYS.forEach(function(key) {
            groups.current[key] = L.layerGroup().addTo(map);
        });
        return function() {
            Object.values(groups.current).forEach(function(g) { map.removeLayer(g); });
            groups.current = {};
        };
    }, [map]); // eslint-disable-line

    useEffect(function() {
        var g = groups.current;
        if (!g.nbr) return;
        LAYER_KEYS.forEach(function(key) {
            if (!g[key]) return;
            var visible = visibleLayers[key];
            if (visible && !map.hasLayer(g[key])) map.addLayer(g[key]);
            if (!visible && map.hasLayer(g[key])) map.removeLayer(g[key]);
        });
    }, [visibleLayers, map]); // eslint-disable-line

    useEffect(function() {
        var g = groups.current;
        if (!g.nbr) return;

        g.nbr.clearLayers();
        nbrBoundaries.forEach(function(geom) {
            if (!geom) return;
            try { L.geoJSON(geom, { style: { color: '#3949ab', weight: 2, fillColor: '#3949ab', fillOpacity: 0.10 } }).addTo(g.nbr); }
            catch(e) {}
        });

        g.iso.clearLayers();
        isoPolygons.forEach(function(ring) {
            if (!ring || !ring.length) return;
            L.polygon(ring.map(function(c) { return [c[1], c[0]]; }), {
                color: '#fb8c00', weight: 2, fillColor: '#fb8c00', fillOpacity: 0.08, dashArray: '6 3'
            }).addTo(g.iso);
        });

        g.geo.clearLayers();
        if (geoAnalysis && geoAnalysis.coordinates) {
            geoAnalysis.coordinates.forEach(function(poly) {
                poly.forEach(function(ring) {
                    L.polygon(ring.map(function(c) { return [c[1], c[0]]; }), {
                        color: '#43a047', weight: 2, fillColor: '#43a047', fillOpacity: 0.08, dashArray: '4 3'
                    }).addTo(g.geo);
                });
            });
        }

        g.intersection.clearLayers();
        if (intersection && intersection.coordinates) {
            intersection.coordinates.forEach(function(poly) {
                poly.forEach(function(ring) {
                    L.polygon(ring.map(function(c) { return [c[1], c[0]]; }), {
                        color: '#6a1b9a', weight: 4, fillColor: '#9c27b0', fillOpacity: 0.30
                    }).addTo(g.intersection);
                });
            });
        }

        g.candidates.clearLayers();
        if (intersection && allNeighborhoods.length) {
            allNeighborhoods.forEach(function(n) {
                try {
                    var geom = toGeometry(n.boundary);
                    if (!geom || !neighborhoodOverlapsIntersection(geom, intersection)) return;
                    L.geoJSON(geom, { style: { color: '#6a1b9a', weight: 2.5, fillColor: '#ce93d8', fillOpacity: 0.35 } })
                        .bindTooltip(n.name, { sticky: true }).addTo(g.candidates);
                } catch(e) {}
            });
        }

        try {
            var b = L.layerGroup(Object.values(g)).getBounds();
            if (b.isValid()) map.fitBounds(b, { padding: [30, 30], maxZoom: 14 });
        } catch(e) {}

    }, [nbrBoundaries, isoPolygons, geoAnalysis, allNeighborhoods, intersection, map]); // eslint-disable-line

    return null;
}

export default function BoundaryOverlayMap({ nbrBoundaries, isoPolygons, geoAnalysis, allNeighborhoods, visibleLayers, onIntersectionReady }) {
    const [intersection, setIntersection] = useState(null);
    const [loading, setLoading] = useState(false);

    // Keep callback in a ref — never a reactive dependency
    const onReadyRef = useRef(onIntersectionReady);
    useEffect(function() { onReadyRef.current = onIntersectionReady; });

    const abortRef = useRef(null);

    // Stable serialised keys — only change when actual geometry data changes
    const nbrKey = useMemo(function() {
        return JSON.stringify(nbrBoundaries.map(function(g) { return g ? g.type + JSON.stringify(g.coordinates).slice(0, 60) : null; }));
    }, [nbrBoundaries]);

    const isoKey = useMemo(function() {
        return JSON.stringify(isoPolygons.length + (isoPolygons[0] ? JSON.stringify(isoPolygons[0]).slice(0, 60) : ''));
    }, [isoPolygons]);

    const geoKey = useMemo(function() {
        return geoAnalysis ? JSON.stringify(geoAnalysis.coordinates).slice(0, 80) : 'null';
    }, [geoAnalysis]);

    useEffect(function() {
        var body = {};
        var nbr = nbrToGeoJSON(nbrBoundaries);
        var iso = isoToGeoJSON(isoPolygons);
        if (nbr) body.nbr = nbr;
        if (iso) body.iso = iso;
        if (geoAnalysis) body.geo = geoAnalysis;

        var sourceCount = Object.keys(body).length;

        if (sourceCount === 0) {
            setIntersection(null);
            if (onReadyRef.current) onReadyRef.current(null);
            return;
        }

        // Single source — no intersection calculation needed
        if (sourceCount === 1) {
            var solo = body.nbr || body.iso || body.geo;
            setIntersection(solo);
            if (onReadyRef.current) onReadyRef.current(solo);
            return;
        }

        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        setLoading(true);

        apiRequest(apiRoutes.geo.polygonIntersection, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + (localStorage.getItem('access_token') || ''),
            },
            body: JSON.stringify(body),
            signal: abortRef.current.signal,
        })
            .then(function(r) { return r.json(); })
            .then(function(data) {
                var result = data.intersection || null;
                setIntersection(result);
                if (onReadyRef.current) onReadyRef.current(result);
            })
            .catch(function(err) {
                if (err.name === 'AbortError') return;
                var fallback = buildSearchBoundaries(nbrBoundaries, isoPolygons, geoAnalysis);
                setIntersection(fallback);
                if (onReadyRef.current) onReadyRef.current(fallback);
            })
            .finally(function() { setLoading(false); });

        return function() { if (abortRef.current) abortRef.current.abort(); };
    }, [nbrKey, isoKey, geoKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div style={{ position: 'relative' }}>
            {loading && <div className="s5gs-intersection-loading">در حال محاسبه تلاقی...</div>}
            <MapContainer center={[35.6892, 51.389]} zoom={11} className="s5gs-overlay-map">
                <LayersControl position="topright">
                    {BASE_LAYERS.map(function(l) {
                        return (
                            <LayersControl.BaseLayer key={l.name} name={l.name} checked={!!l.checked}>
                                <TileLayer url={l.url} attribution={l.attribution} />
                            </LayersControl.BaseLayer>
                        );
                    })}
                </LayersControl>
                <OverlayLayers
                    nbrBoundaries={nbrBoundaries}
                    isoPolygons={isoPolygons}
                    geoAnalysis={geoAnalysis}
                    allNeighborhoods={allNeighborhoods}
                    visibleLayers={visibleLayers}
                    intersection={intersection}
                />
            </MapContainer>
        </div>
    );
}
